import React, { useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  AnimatePresence,
} from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Button from "./Button";
import ThemeToggle from "./ThemeToggle";
import logoSrc from "../letsloop-logo.png";

interface NavbarProps {
  onNavigate?: (page: string) => void;
  currentPage?: string;
}

const Navbar: React.FC<NavbarProps> = ({ onNavigate, currentPage = "" }) => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Shrink effect on scroll (Desktop only)
  const scale = useTransform(scrollY, [0, 100], [1, 0.96]);
  const y = useTransform(scrollY, [0, 100], [0, 8]);

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 50);
  });

  // `route` items are real react-router routes (standalone pages);
  // the rest are in-page sections swapped via onNavigate/currentPage.
  const navItems: { id: string; label: string; route?: string }[] = [
    { id: "home", label: "Home", route: "/" },
    // The product page was reachable only from body copy and the footer, so
    // someone who wanted to know what LetsLoop actually does had to scroll the
    // whole story to find out. It is the most-asked question on the site, so it
    // sits second, right after Home.
    { id: "how-it-works", label: "How it works", route: "/how-it-works" },
    { id: "mission", label: "Mission", route: "/#mesh" },
    { id: "about", label: "About", route: "/about" },
    { id: "blog", label: "News", route: "/news" },
  ];

  const handleNavClick = (item: { id: string; route?: string }) => {
    setIsMobileMenuOpen(false);

    // A route carrying a hash targets a SECTION, so it must not be followed by
    // the scroll-to-top below. "Mission" previously had no route at all: it fell
    // through to navigate("/"), which on the story home page looked like a dead
    // button. It now points at the mesh section, which is where the mission
    // actually lives on that page.
    if (item.route?.includes("#")) {
      const [path, hash] = item.route.split("#");
      navigate(path || "/");
      // Wait for the route to commit before looking for the target.
      window.setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 60);
      return;
    }

    if (item.route) {
      navigate(item.route);
    } else if (onNavigate) {
      onNavigate(item.id);
    } else {
      navigate("/");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDownloadClick = () => {
    setIsMobileMenuOpen(false);
    navigate("/download");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <nav
        className="fixed left-0 right-0 z-50 flex justify-center px-4 pointer-events-none"
        // Now that viewport-fit=cover lets the page run under the status bar,
        // a flat 24px offset would put this pill beneath the Dynamic Island.
        style={{ top: "max(1.5rem, env(safe-area-inset-top, 0px))" }}
      >
        <motion.div
          style={{ scale, y }}
          className="pointer-events-auto origin-top w-full flex justify-center"
        >
          {/* Main Navbar Background - Using #FFFFFB for consistency with other sections */}
          <div className="bg-canvas shadow-[0_4px_24px_rgba(0,0,0,0.10)] rounded-full px-6 py-3 flex items-center justify-between gap-6 md:gap-10 w-full md:w-auto md:min-w-[320px] md:max-w-4xl transition-all duration-300">
            {/* Logo */}
            <button
              onClick={() => handleNavClick({ id: "home" })}
              className="font-bold tracking-tighter text-lg flex items-center gap-2 group z-50 relative shrink-0 hover:opacity-80 transition-opacity"
            >
              <img
                src={logoSrc}
                alt="LetsLoop"
                width={100}
                height={22}
                decoding="async"
                style={{ width: "100px", height: "auto" }}
              />
            </button>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-7 shrink-0 text-sm font-medium text-gray-500">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className={`whitespace-nowrap hover:text-ink transition-colors relative group ${currentPage === item.id ? "text-ink" : ""}`}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-[1px] bg-ink transition-all duration-300 ${currentPage === item.id ? "w-full" : "w-0 group-hover:w-full"}`}
                  ></span>
                </button>
              ))}
            </div>

            {/* Desktop: theme toggle + CTA */}
            <div className="hidden md:flex items-center gap-3 shrink-0">
              <ThemeToggle className="w-9 h-9" />
              <Button
                variant="primary"
                size="sm"
                isMagnetic={false}
                className="h-10 text-sm font-semibold px-5"
                onClick={handleDownloadClick}
              >
                Download
              </Button>
            </div>

            {/* Mobile: theme toggle + menu (toggle stays reachable without opening the menu) */}
            <div className="md:hidden relative z-50 flex items-center gap-2">
              <ThemeToggle className="w-10 h-10 bg-gray-100" />
              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-salmon/10 hover:text-salmon transition-colors text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {isMobileMenuOpen ? (
                  <X size={20} aria-hidden="true" />
                ) : (
                  <Menu size={20} aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-paper pt-32 px-6 flex flex-col md:hidden"
          >
            <div className="flex flex-col gap-6 text-2xl font-medium tracking-tight">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="py-4 border-b border-gray-100 flex justify-between items-center group text-left"
                >
                  {item.label}
                  <span
                    className={`w-2 h-2 rounded-full bg-salmon ${currentPage === item.id ? "opacity-100" : "opacity-0"} group-hover:opacity-100 transition-opacity`}
                  ></span>
                </button>
              ))}
            </div>

            <div className="mt-12">
              <Button
                className="w-full h-14 text-lg"
                onClick={handleDownloadClick}
              >
                Download
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
